import { useState, useMemo, useEffect, useCallback, useRef, useContext, type MouseEvent } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useDisclosure, useToast } from '@chakra-ui/react';
import { FiList, FiFlag, FiCheck } from 'react-icons/fi';
import { TbArchive, TbHistory } from 'react-icons/tb';
import {
    useInvoicesFeed,
    InvoiceAdditionalFilters,
    type InvoiceEntry,
} from '../../../hooks/invoice/useInvoicesFeed';
import { useRecentActivity } from '../../../hooks/useRecentActivity';
import { useTableSettings } from '../../../hooks/useTableSettings';
import { useColumnWidths } from '../../../hooks/useColumnWidths';
import { useScrollShadow } from '../../../hooks/useScrollShadow';
import { useResponsiveToolbar } from '../../../hooks/useResponsiveToolbar';
import { useGetSettings } from '../../../hooks/useGetSettings';
import {
    getNexReconciliationIdentifierModeFromSettings,
} from '../../../utils/invoiceNexIdentifierUi';
import { useArchiveInvoices } from '../../../hooks/invoice/useArchiveInvoices';
import { useResetInvoicingDemoData } from '../../../hooks/invoice/useResetInvoicingDemoData';
import {
    handleOrderBulkRowClick,
    useOrderBulkSelection,
} from '../../../hooks/useOrderBulkSelection';
import { isInvoicingDemoSession } from '../../../demo/invoicingDemo';
import SessionContext from '../../../contexts/SessionContext';
import {
    buildInvoicesListSearch,
    invoicesListSearchEquals,
    invoicesListPageHasNext,
    invoicesSidebarTabFromViewState,
    INVOICES_LIST_FETCH_LIMIT,
    INVOICES_LIST_PAGE_SIZE,
    parseInvoicesListSearchParams,
    readInvoicesViewStateFromLocation,
    resolveInitialInvoicesViewState,
    saveInvoicesViewStateCache,
    visibleInvoicesOnListPage,
    type InvoicesListType,
    type InvoicesViewStateCache,
} from '../../../shared/invoicesListViewState';
import { recallListSearch } from '../../../shared/listSearchMemory';
import { useRememberListSearch } from '../../../hooks/useRememberListSearch';

export const INVOICES_LIST_TAB_ICONS: Record<string, React.ElementType> = {
    all: FiList as React.ElementType,
    flagged: FiFlag as React.ElementType,
    submitted: FiCheck as React.ElementType,
    archived: TbArchive as React.ElementType,
};

export const INVOICES_ACTIVITY_BUTTON_ICON: React.ElementType = TbHistory as React.ElementType;

export function useInvoicesListPage() {
    const { data: orgSettings } = useGetSettings();
    const nexReconciliationIdentifierMode = useMemo(
        () => getNexReconciliationIdentifierModeFromSettings(orgSettings),
        [orgSettings],
    );

    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const sessionContext = useContext(SessionContext);
    const isInvoicingDemo = isInvoicingDemoSession(sessionContext);
    const { trackView } = useRecentActivity();
    const toast = useToast();
    const archiveInvoicesMutation = useArchiveInvoices();
    const resetInvoicingDemoMutation = useResetInvoicingDemoData();
    const { settings: tableSettings, updateSettings: updateTableSettings, tableSettingsSx } =
        useTableSettings('invoicing');
    const { widths: colWidths, updateWidth: updateColWidth, resetWidths: resetColWidths } =
        useColumnWidths('invoicing');
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const { showRightShadow } = useScrollShadow(scrollContainerRef);
    const { containerRef: toolbarRef, locationCompact, searchCompact } = useResponsiveToolbar();

    const initialViewState = useMemo(
        () => resolveInitialInvoicesViewState({
            locationState: location.state,
            searchParams,
            rememberedSearch: recallListSearch(location.key),
        }),
        // Resolve once on mount so later URL writes do not re-initialize.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const [type, setType] = useState<InvoicesListType>(initialViewState.type);
    const [locationIds, setLocationIds] = useState<Array<string> | 'all'>(initialViewState.locationIds);
    const [searchQuery, setSearchQuery] = useState(initialViewState.searchQuery);
    const [debouncedSearch, setDebouncedSearch] = useState(initialViewState.debouncedSearch);
    const [showArchived, setShowArchived] = useState(initialViewState.showArchived);
    const [showNegotiation, setShowNegotiation] = useState(initialViewState.showNegotiation === true);
    const [selectedNegotiationId, setSelectedNegotiationId] = useState<string | null>(
        initialViewState.selectedNegotiationId ?? null,
    );
    const [currentPage, setCurrentPage] = useState(initialViewState.currentPage);
    const [additionalFilters, setAdditionalFilters] = useState<InvoiceAdditionalFilters>(
        initialViewState.additionalFilters,
    );
    const [hasHydratedUrl, setHasHydratedUrl] = useState(false);
    const searchChangeIsLocal = useRef(false);
    // The last list URL this hook wrote (or accepted). URL changes that match it
    // are our own echoes; anything else is external navigation to apply.
    const lastSyncedSearch = useRef<string | null>(null);
    // A search restored by history is not a typed edit: set both values so the
    // debounce does not reset the page.
    const restoreSearch = useCallback((search: string) => {
        setSearchQuery(search);
        setDebouncedSearch(search);
    }, []);
    useRememberListSearch(searchQuery, restoreSearch);
    const { isOpen: isCreateModalOpen, onOpen: onCreateModalOpen, onClose: onCreateModalClose } =
        useDisclosure();
    const { isOpen: isReasonHelpOpen, onOpen: onReasonHelpOpen, onClose: onReasonHelpClose } =
        useDisclosure();
    const { isOpen: isStatusHelpOpen, onOpen: onStatusHelpOpen, onClose: onStatusHelpClose } =
        useDisclosure();
    const {
        isOpen: isPartsInvoicingGuideOpen,
        onOpen: onPartsInvoicingGuideOpen,
        onClose: onPartsInvoicingGuideClose,
    } = useDisclosure();
    const {
        isOpen: isActivityLogOpen,
        onOpen: onActivityLogOpen,
        onClose: onActivityLogClose,
    } = useDisclosure();
    const {
        isOpen: isDemoResetOpen,
        onOpen: onDemoResetOpen,
        onClose: onDemoResetClose,
    } = useDisclosure();

    const limit = INVOICES_LIST_PAGE_SIZE;
    const queryLimit = INVOICES_LIST_FETCH_LIMIT;

    const currentViewState = useMemo<InvoicesViewStateCache>(() => ({
        type,
        showArchived,
        showNegotiation,
        selectedNegotiationId,
        currentPage,
        searchQuery,
        debouncedSearch,
        additionalFilters,
        locationIds,
    }), [type, showArchived, showNegotiation, selectedNegotiationId, currentPage, searchQuery, debouncedSearch, additionalFilters, locationIds]);

    const applyViewState = useCallback((next: InvoicesViewStateCache) => {
        setType((prev) => (prev === next.type ? prev : next.type));
        setShowArchived((prev) => (prev === next.showArchived ? prev : next.showArchived));
        setShowNegotiation((prev) => {
            const nextVal = next.showNegotiation === true;
            return prev === nextVal ? prev : nextVal;
        });
        setSelectedNegotiationId((prev) => {
            const nextVal = next.selectedNegotiationId ?? null;
            return prev === nextVal ? prev : nextVal;
        });
        setCurrentPage((prev) => (prev === next.currentPage ? prev : next.currentPage));
        setSearchQuery((prev) => (prev === next.searchQuery ? prev : next.searchQuery));
        setDebouncedSearch((prev) => (prev === next.debouncedSearch ? prev : next.debouncedSearch));
        setAdditionalFilters((prev) => (
            JSON.stringify(prev) === JSON.stringify(next.additionalFilters) ? prev : next.additionalFilters
        ));
        setLocationIds((prev) => (
            JSON.stringify(prev) === JSON.stringify(next.locationIds) ? prev : next.locationIds
        ));
    }, []);

    useEffect(() => {
        const desired = buildInvoicesListSearch({ type, showArchived, showNegotiation, selectedNegotiationId, currentPage });
        lastSyncedSearch.current = desired;
        setSearchParams((previous) => (
            invoicesListSearchEquals(desired, previous.toString())
                ? previous
                : new URLSearchParams(desired)
        ), { replace: true });
        setHasHydratedUrl(true);
        // Hydrate the list URL from restored tab/page exactly once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // State -> URL. Runs before the URL -> state effect below so a state change
    // records its URL as synced first; the echo of that write is then ignored
    // instead of pushing the previous URL's values back into state.
    useEffect(() => {
        if (!hasHydratedUrl) return;
        const desired = buildInvoicesListSearch({
            type,
            showArchived,
            showNegotiation,
            selectedNegotiationId,
            currentPage,
        });
        lastSyncedSearch.current = desired;
        setSearchParams((previous) => (
            invoicesListSearchEquals(desired, previous.toString())
                ? previous
                : new URLSearchParams(desired)
        ), { replace: true });
    }, [type, showArchived, showNegotiation, selectedNegotiationId, currentPage, hasHydratedUrl, setSearchParams]);

    useEffect(() => {
        if (!hasHydratedUrl) return;
        const fromLocation = readInvoicesViewStateFromLocation(location.state);
        if (!fromLocation) return;
        applyViewState(fromLocation);
        // Consume the payload and land on the URL for the handed-over state, not
        // the stale one this entry had, so the URL -> state effect has nothing
        // to undo.
        const desired = buildInvoicesListSearch(fromLocation);
        lastSyncedSearch.current = desired;
        navigate(
            { pathname: location.pathname, search: desired ? `?${desired}` : '' },
            { replace: true, state: {} },
        );
    }, [applyViewState, hasHydratedUrl, location.pathname, location.state, navigate]);

    // URL -> state, for external navigation only (back/forward, sidebar deep link).
    useEffect(() => {
        if (!hasHydratedUrl) return;
        if (readInvoicesViewStateFromLocation(location.state)) return;
        const current = searchParams.toString();
        if (lastSyncedSearch.current !== null && invoicesListSearchEquals(current, lastSyncedSearch.current)) return;
        lastSyncedSearch.current = current;
        const fromUrl = parseInvoicesListSearchParams(searchParams);
        setType((previous) => (previous === fromUrl.type ? previous : fromUrl.type));
        setShowArchived((previous) => (previous === fromUrl.showArchived ? previous : fromUrl.showArchived));
        setShowNegotiation((previous) => {
            const next = fromUrl.showNegotiation === true;
            return previous === next ? previous : next;
        });
        setSelectedNegotiationId((previous) => {
            const next = fromUrl.selectedNegotiationId ?? null;
            return previous === next ? previous : next;
        });
        setCurrentPage((previous) => (previous === fromUrl.currentPage ? previous : fromUrl.currentPage));
    }, [hasHydratedUrl, location.state, searchParams]);

    useEffect(() => {
        saveInvoicesViewStateCache(currentViewState);
    }, [currentViewState]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch((previous) => {
                if (previous === searchQuery) return previous;
                searchChangeIsLocal.current = true;
                return searchQuery;
            });
        }, 500);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    useEffect(() => {
        if (!searchChangeIsLocal.current) return;
        searchChangeIsLocal.current = false;
        setCurrentPage(1);
    }, [debouncedSearch]);

    useEffect(() => {
        const label = `Parts Invoicing • ${type[0].toUpperCase() + type.slice(1)}`;
        trackView({
            type: 'invoices_tab',
            key: `invoices_tab:${type}`,
            label,
            path: '/invoices',
            meta: { tab: type },
        });
    }, [trackView, type]);

    const {
        data: invoicesData,
        isLoading,
        isRefetching,
        error,
        refetch,
    } = useInvoicesFeed(
        type,
        showArchived ? 'archived' : 'active',
        locationIds,
        currentPage,
        queryLimit,
        debouncedSearch,
        additionalFilters,
    );

    const { pageInvoices, hasNextPage } = useMemo(() => {
        const fetched = invoicesData?.invoices || [];
        return {
            pageInvoices: visibleInvoicesOnListPage(fetched),
            hasNextPage: invoicesListPageHasNext(fetched.length),
        };
    }, [invoicesData?.invoices]);

    const pageInvoiceIds = useMemo(() => pageInvoices.map((inv) => inv._id), [pageInvoices]);
    const {
        selectedOrderIds: selectedInvoiceIds,
        toggleOrderSelected: toggleInvoiceSelected,
        toggleSelectAllOnPage,
        allPageSelected,
        isPageSelectionIndeterminate,
        deselectOrder,
        clearSelection,
    } = useOrderBulkSelection({ orderIds: pageInvoiceIds });

    useEffect(() => {
        clearSelection();
    }, [currentPage, type, showArchived, debouncedSearch, locationIds, additionalFilters, clearSelection]);

    const totalPages = useMemo(() => {
        const totalInvoices = invoicesData?.totalInvoices ?? 0;
        const computedTotalPages = Math.ceil(totalInvoices / limit);
        return Math.max(1, computedTotalPages || currentPage);
    }, [invoicesData?.totalInvoices, limit, currentPage]);

    const openInvoice = useCallback(
        (invoiceId: string) => {
            saveInvoicesViewStateCache(currentViewState);
            navigate(`/invoices/${invoiceId}`, {
                state: {
                    invoicesViewState: currentViewState,
                    source: invoicesSidebarTabFromViewState(currentViewState),
                },
            });
        },
        [currentViewState, navigate],
    );

    const handleRowClick = (invoiceId: string, event: MouseEvent, rowIndex: number) => {
        handleOrderBulkRowClick(
            event,
            invoiceId,
            rowIndex,
            toggleInvoiceSelected,
            () => openInvoice(invoiceId),
            true,
        );
    };

    const handleRefresh = useCallback(() => {
        refetch();
    }, [refetch]);

    const handleTypeChange = (newType: InvoicesListType) => {
        setType(newType);
        setShowArchived(false);
        setShowNegotiation(false);
        setSelectedNegotiationId(null);
        setSearchQuery('');
        setDebouncedSearch('');
        setCurrentPage(1);
    };

    const handleToggleArchived = () => {
        setShowArchived((previous) => !previous);
        setShowNegotiation(false);
        setSelectedNegotiationId(null);
        setCurrentPage(1);
    };

    const handleToggleNegotiation = () => {
        setShowNegotiation((previous) => {
            const next = !previous;
            if (next) {
                setShowArchived(false);
            } else {
                setSelectedNegotiationId(null);
            }
            return next;
        });
        setCurrentPage(1);
    };

    const handleSelectNegotiation = useCallback((negotiationId: string) => {
        setShowNegotiation(true);
        setShowArchived(false);
        setSelectedNegotiationId(negotiationId);
    }, []);

    const handleLocationChange = useCallback(
        (newLocationIds: Array<string> | 'all') => {
            setLocationIds(newLocationIds);
            setCurrentPage(1);
        },
        [],
    );

    const handleFiltersChange = useCallback(
        (newFilters: InvoiceAdditionalFilters) => {
            setAdditionalFilters(newFilters);
            setCurrentPage(1);
        },
        [],
    );

    const handleToggleArchiveInvoice = async (invoice: InvoiceEntry) => {
        try {
            await archiveInvoicesMutation.mutateAsync({
                invoiceId: invoice._id,
                archived: !invoice.archived,
            });
            deselectOrder(invoice._id);
            toast({
                status: 'success',
                title: invoice.archived ? 'Invoice restored' : 'Invoice archived',
                duration: 2000,
                isClosable: true,
            });
        } catch {
            toast({
                status: 'error',
                title: 'Failed to update archive state',
                duration: 2500,
                isClosable: true,
            });
        }
    };

    const handleBulkArchiveToggle = async () => {
        const ids = Array.from(selectedInvoiceIds);
        if (ids.length === 0) return;
        const archived = !showArchived;
        try {
            await archiveInvoicesMutation.mutateAsync({ invoiceIds: ids, archived });
            toast({
                status: 'success',
                title: archived
                    ? `Archived ${ids.length} invoice${ids.length === 1 ? '' : 's'}`
                    : `Restored ${ids.length} invoice${ids.length === 1 ? '' : 's'}`,
                duration: 2500,
                isClosable: true,
            });
            clearSelection();
        } catch {
            toast({
                status: 'error',
                title: 'Failed to update archive state',
                duration: 2500,
                isClosable: true,
            });
        }
    };

    const handleBulkAction = (actionId: string) => {
        if (actionId === 'archive' || actionId === 'unarchive') {
            void handleBulkArchiveToggle();
        }
    };

    const handleConfirmDemoReset = useCallback(async () => {
        try {
            await resetInvoicingDemoMutation.mutateAsync();
            toast({
                status: 'success',
                title: 'Demo invoicing data reset',
                description: 'Restored the frozen production snapshot.',
                duration: 3000,
                isClosable: true,
            });
            onDemoResetClose();
            clearSelection();
            handleRefresh();
        } catch {
            toast({
                status: 'error',
                title: 'Failed to reset demo data',
                duration: 3500,
                isClosable: true,
            });
        }
    }, [clearSelection, handleRefresh, onDemoResetClose, resetInvoicingDemoMutation, toast]);

    return {
        invoicesData,
        isInvoicingDemo,
        nexReconciliationIdentifierMode,
        type,
        showArchived,
        showNegotiation,
        selectedNegotiationId,
        searchQuery,
        setSearchQuery,
        locationIds,
        additionalFilters,
        currentPage,
        setCurrentPage,
        selectedInvoiceIds,
        pageInvoices,
        hasNextPage,
        totalPages,
        isLoading,
        isRefetching,
        error,
        allPageSelected,
        isPageSelectionIndeterminate,
        clearSelection,
        tableSettings,
        updateTableSettings,
        tableSettingsSx,
        colWidths,
        updateColWidth,
        resetColWidths,
        scrollContainerRef,
        showRightShadow,
        toolbarRef,
        locationCompact,
        searchCompact,
        archiveInvoicesMutation,
        resetInvoicingDemoMutation,
        handleRowClick,
        handleRefresh,
        handleTypeChange,
        handleToggleArchived,
        handleToggleNegotiation,
        handleSelectNegotiation,
        handleLocationChange,
        handleFiltersChange,
        handleToggleArchiveInvoice,
        toggleInvoiceSelected,
        toggleSelectAllOnPage,
        handleBulkAction,
        handleConfirmDemoReset,
        isCreateModalOpen,
        onCreateModalOpen,
        onCreateModalClose,
        isReasonHelpOpen,
        onReasonHelpOpen,
        onReasonHelpClose,
        isStatusHelpOpen,
        onStatusHelpOpen,
        onStatusHelpClose,
        isPartsInvoicingGuideOpen,
        onPartsInvoicingGuideOpen,
        onPartsInvoicingGuideClose,
        isActivityLogOpen,
        onActivityLogOpen,
        onActivityLogClose,
        isDemoResetOpen,
        onDemoResetOpen,
        onDemoResetClose,
    };
}

export type InvoicesListPageModel = ReturnType<typeof useInvoicesListPage>;
